import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

import { MatCardModule } from '@angular/material/card';
import { MatTableModule } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatDividerModule } from '@angular/material/divider';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressBarModule } from '@angular/material/progress-bar';

import { ApiService } from '../services/api.service';
import { extractList } from '../shared/utils/extract-list';

interface PerformanceReview {
  id: number;
  employeeId: number;
  employeeName: string;
  department: string;
  reviewPeriod: string;
  rating: number;
  goals: string;
  feedback: string;
  status: string;
}

@Component({
  selector: 'app-performance',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatCardModule,
    MatTableModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatDividerModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    MatProgressBarModule
  ],
  templateUrl: './performance.component.html',
  styleUrl: './performance.component.scss'
})
export class PerformanceComponent implements OnInit {

  loading = false;
  searchText = '';

  reviews: PerformanceReview[] = [];
  filteredReviews: PerformanceReview[] = [];

  avgRating = 0;
  completedReviews = 0;
  inProgressReviews = 0;

  displayedColumns: string[] = ['employee', 'department', 'period', 'rating', 'status'];

  constructor(private apiService: ApiService) {}

  ngOnInit(): void {
    this.loadPerformanceReviews();
  }

  loadPerformanceReviews(): void {
    this.loading = true;

    this.apiService.getPerformanceReviews().subscribe({
      next: (response) => {
        const data = extractList(response);
        this.reviews = data.map((review: any) => ({
          id: review.id || Math.random(),
          employeeId: review.employeeId || 0,
          employeeName:
            review.employeeName ||
            review.employee?.displayName ||
            review.employee?.name ||
            `Employee #${review.employeeId || 'N/A'}`,
          department: review.department || review.departmentName || 'N/A',
          reviewPeriod: review.reviewPeriod || review.period || 'N/A',
          rating: review.rating || review.overallRating || 0,
          goals: review.goals || '',
          feedback: review.feedback || '',
          status: review.status || 'In Progress'
        }));
        this.filteredReviews = [...this.reviews];
        this.calculateStats();
        this.loading = false;
      },
      error: () => {
        this.loading = false;
      }
    });
  }

  calculateStats(): void {
    this.avgRating = this.reviews.length > 0
      ? this.reviews.reduce((sum, r) => sum + r.rating, 0) / this.reviews.length
      : 0;
    this.completedReviews = this.reviews.filter(r => r.status === 'Completed').length;
    this.inProgressReviews = this.reviews.filter(r => r.status === 'In Progress').length;
  }

  filterReviews(): void {
    const search = this.searchText.toLowerCase().trim();
    this.filteredReviews = this.reviews.filter(r =>
      r.employeeName.toLowerCase().includes(search) ||
      r.department.toLowerCase().includes(search) ||
      r.reviewPeriod.toLowerCase().includes(search)
    );
  }

  getRatingColor(rating: number): string {
    if (rating >= 4.5) return '#159957';
    if (rating >= 4.0) return '#356ae6';
    if (rating >= 3.0) return '#e99a00';
    return '#dc3545';
  }
}